// 管理者: 監査ログ(設計書 §3.5.6)
// JSON 一覧取得 + CSV エクスポート(認証ヘッダー付き fetch → blob → ダウンロード)

import { useQuery } from '@tanstack/react-query';
import { apiRequestRaw } from '../client';
import { STORAGE_KEYS } from '../../lib/storage';

export type ActorType = 'admin' | 'performer' | 'system' | 'agent';
export type AuditCategory =
  | 'auth'
  | 'config_change'
  | 'approval'
  | 'session'
  | 'system'
  | 'admin_action';
export type AuditSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface AuditLogEntry {
  id: number;
  occurred_at: string;
  actor_type: ActorType;
  actor_id: string | null;
  actor_label: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  ip: string | null;
  user_agent: string | null;
  category: AuditCategory;
  severity: AuditSeverity;
  message: string;
  details: Record<string, unknown> | null;
}

export interface AuditLogsResponse {
  data: AuditLogEntry[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

export interface AuditLogsQuery {
  page?: number;
  per_page?: number;
  q?: string;
  actor_type?: ActorType;
  category?: AuditCategory;
  severity?: AuditSeverity;
  target_type?: string;
  target_id?: string;
  from?: string;
  to?: string;
}

function buildQueryString(params: AuditLogsQuery): string {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per_page) qs.set('per_page', String(params.per_page));
  if (params.q) qs.set('q', params.q);
  if (params.actor_type) qs.set('actor_type', params.actor_type);
  if (params.category) qs.set('category', params.category);
  if (params.severity) qs.set('severity', params.severity);
  if (params.target_type) qs.set('target_type', params.target_type);
  if (params.target_id) qs.set('target_id', params.target_id);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  return qs.toString();
}

export const adminAuditLogKeys = {
  list: (q: AuditLogsQuery) => ['admin', 'audit-logs', q] as const,
};

export function useAuditLogs(params: AuditLogsQuery) {
  return useQuery({
    queryKey: adminAuditLogKeys.list(params),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const path = qs ? `/admin/audit-logs?${qs}` : '/admin/audit-logs';
      return await apiRequestRaw<AuditLogsResponse>(path);
    },
    placeholderData: (prev) => prev,
  });
}

// CSV エクスポート: 認証ヘッダー必須なので fetch + blob 経由でダウンロード。
export async function downloadAuditLogsCsv(params: AuditLogsQuery): Promise<void> {
  const qs = buildQueryString(params);
  const url = '/v1/admin/audit-logs/export' + (qs ? `?${qs}` : '');
  const token = localStorage.getItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`CSV エクスポートに失敗しました (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const filename = parseFilename(res.headers.get('content-disposition')) ?? buildFallbackFilename();
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

function parseFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function buildFallbackFilename(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `audit-log-${yyyy}${mm}${dd}.csv`;
}
